import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { Subscription, timer } from 'rxjs'
import { finalize, switchMap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { RecoveryPointDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { RpSliderChangedEvent } from 'src/app/shared/rp-range-slider/rp-range-slider.component'
import { shortTimeString } from 'src/app/shared/utils'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { uniquify } from 'src/utils/uniquify'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'
import { ResourceType } from 'src/utils/resource-types'
import { isTopmostModal, popModal, pushModal } from 'src/app/shared/modal-dialog/modal-key-stack'

export interface LaunchRestoreSettings {
  application: Application
  recoveryPointId?: string
  isRealTime?: boolean
  networkSandboxEnabled?: boolean
  networkSandboxWhitelist?: string[]
  networkSandboxAllowedDomains?: string[]
  networkSandboxAllowedServices?: string[]
  quarantineEnabled?: boolean
  advancedNetworkSandbox?: boolean
  forceNewTargetPlan?: boolean
  protectRecoveryPoints?: boolean
  // Set when the user submitted a conclude-test for an app whose
  // restore hadn't completed yet. The parent dispatch overrides the shared
  // outcome/notes with a fixed cancel-test result for these rows so a mixed
  // batch records the right semantics per app.
  cancelledMidRestore?: boolean
}

@Directive()
export abstract class LaunchSyncDialogBase implements OnInit, OnDestroy {
  constructor(
    private applicationProviderService: ApplicationProviderService,
    protected accountService: AccountService,
    protected apiService: ApiService,
    protected uiService: UiService,
    protected accessLevelService: AccessLevelService
  ) {}

  // Scopes scrollToStep DOM lookups to this component instance so concurrently
  // mounted dialogs (e.g. confirm overlay above launch-test) don't collide on
  // shared step IDs.
  private elementRef = inject(ElementRef)

  // Hosted inside an OnPush ancestor (application.component); async subscription
  // resolutions don't trigger CD on this subtree without an explicit dirty mark.
  protected cdr = inject(ChangeDetectorRef)

  private applicationSubscription: Subscription = null
  // Per-app live update subscription so status pills / disclaimers update as
  // restores progress without the user having to close-and-reopen the dialog.
  private applicationUpdateSubscription: Subscription = null
  protected _applications: Array<Application> = null
  private _refreshSubscriptions: Map<string, Subscription> = null
  private _destroyed: boolean = false
  protected selectedApplicationIds: Set<string> = null
  private selectedRecoveryPoint: RecoveryPointDto = null
  protected isRealTime: boolean = false
  protected onlyProtectedRps: boolean = false
  private canAdjustRecoveryPointSticky: boolean = null
  private appPermissionsMapCache: Map<string, Set<string>> | null = null
  private isLegacyAdminCache: boolean = false

  @Input() application: Application
  @Input() launching: boolean = false
  @Output() cancel = new EventEmitter<null>()

  applicationSearchFilter: string = ''

  // Only one step is expanded at a time; `null` means all collapsed. Default
  // to 'applications' so the modal opens with the first step already in view.
  activeStepKey: string | null = 'applications'

  // Used for unit tests.
  setApplication(app: Application) {
    this.application = app
  }

  // "All selected" is scoped to the currently visible (filtered) list so the
  // toggle reads correctly while a search filter is active. Without this,
  // hidden selections would make the label lie about the visible rows.
  areAllApplicationsSelected(): boolean {
    const visible = this.applications
    if (visible.length === 0) return false
    return visible.every((app) => this.selectedApplicationIds.has(app.applicationObj.applicationId))
  }

  // Only toggle the currently visible (filtered) applications so that rows
  // hidden by the search filter keep their prior selection state.
  toggleSelectAllApplications(checked: boolean): void {
    for (const app of this.applications) {
      if (checked) {
        this.selectedApplicationIds.add(app.applicationObj.applicationId)
      } else {
        this.selectedApplicationIds.delete(app.applicationObj.applicationId)
      }
    }
  }

  ngOnInit() {
    pushModal(this)

    // initialize the selected recovery point.  This will get overridden immediately by the recovery point slider,
    // but in some cases (i.e. locked recovery point), the slider doesn't show and this doesn't get
    // updated there.
    // Pick the latest recovery point.
    this.selectedRecoveryPoint = this.application.recoveryPoints.reduce(
      (accum, curr) => (!!accum ? (accum.timestamp > curr.timestamp ? accum : curr) : curr),
      null
    )
    this.isRealTime = this.application.rpo === 0

    // build the list of applications that should display in the UI
    this.selectedApplicationIds = new Set<string>()
    this.selectedApplicationIds.add(this.application.applicationId)

    let that = this
    function isSyncableApplication(application: Application) {
      // Only the regions need to match for multi-account
      return (
        application.sourceRegion === that.application.sourceRegion &&
        application.targetRegion === that.application.targetRegion
      )
    }

    // Get the currently-loaded applications from the application provider service and filter by region
    this._applications = this.applicationProviderService
      .loadedApplications()
      .filter((app) => isSyncableApplication(app))

    // Load and cache user permissions for filtering applications
    this.accessLevelService.getUserPermissions().subscribe({
      next: (permissions) => {
        this.appPermissionsMapCache = permissions.appPermissionsMap || new Map()
        this.isLegacyAdminCache = permissions.isLegacyAdmin
        this.cdr.markForCheck()
      },
      // Default to empty permissions on error so the dialog exits loading state.
      // Server-side enforcement still prevents unauthorized actions.
      error: () => {
        this.appPermissionsMapCache = new Map()
        this.isLegacyAdminCache = false
        this.cdr.markForCheck()
      },
    })

    // Hosted inside an OnPush ancestor; restore polling updates Application
    // instances in place but doesn't propagate CD into this subtree. Subscribe
    // to the global app publish subject and mark for check so status pills /
    // cancel-vs-conclude routing reflect live restore progress.
    this.applicationUpdateSubscription = Application.publishSubject$.subscribe(() => {
      if (this._destroyed) return
      this.cdr.markForCheck()
    })

    // Always refresh recovery points when dialog opens to ensure we have the latest data.
    // This prevents issues where stale cached RPs are displayed if the user has had the app
    // open for a while without refreshing. Using stale RPs can cause test/failover failures
    // if an RP was deleted between when it was cached and when the test is triggered.
    this._refreshSubscriptions = new Map<string, Subscription>()
    if (this.refreshRecoveryPointsOnOpen) {
      this._applications.forEach((app) => {
        // Use timer() to launch this asynchronously.  Otherwise, it can complete
        // before the subscription is added to the map and we get an error when
        // we try to delete it.
        const refreshObservable = timer(0).pipe(
          switchMap(() => app.refreshRecoveryPoints$()),
          finalize(() => {
            this._refreshSubscriptions.delete(app.applicationId)
            // Suppress when the dialog has already torn down — finalize fires
            // on unsubscribe (in ngOnDestroy) too, and we don't want hooks
            // running on a dead component.
            if (this._destroyed) return
            this.onRecoveryPointsRefreshed(app)
            this.cdr.markForCheck()
          })
        )
        this._refreshSubscriptions.set(app.applicationId, refreshObservable.subscribe())
      })
    }
  }

  // Subclasses that don't pick a new recovery point (sandbox config, conclude,
  // failback) override this to skip the per-app RP fan-out — those flows act on
  // already-running state and don't depend on freshly-listed RPs.
  protected get refreshRecoveryPointsOnOpen(): boolean {
    return true
  }

  // Hook called after each app's initial RP refresh resolves. Default no-op;
  // subclasses override to react to freshly-loaded RP stats (e.g. re-evaluate
  // form-control disabled state that depends on stats from those RPs).
  protected onRecoveryPointsRefreshed(_application: Application): void {}

  ngOnDestroy() {
    // Set first so finalize callbacks triggered by the unsubscribes below
    // see _destroyed=true and skip post-teardown hook work.
    this._destroyed = true
    popModal(this)

    // Cancel any in-flight per-app RP refreshes so a late HTTP response
    // doesn't fire downstream hooks on a dead component.
    this._refreshSubscriptions?.forEach((sub) => sub.unsubscribe())
    this._refreshSubscriptions?.clear()

    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
    }
    this.applicationSubscription = null

    if (subscriptionActive(this.applicationUpdateSubscription)) {
      this.applicationUpdateSubscription.unsubscribe()
    }
    this.applicationUpdateSubscription = null
  }

  protected isTopmostModal(): boolean {
    return isTopmostModal(this)
  }

  get applications() {
    const searchTerm = this.applicationSearchFilter?.trim().toLowerCase() ?? ''
    // `_applications` is populated in ngOnInit; guard against early template
    // reads so `toggleSelectAllApplications` / templates never NPE pre-init.
    return (this._applications ?? [])
      .filter((app) => this.applicationIsCandidate(app) && this.hasRequiredAccessForOperation(app.applicationId))
      .filter((app) => !searchTerm || app.name?.toLowerCase().includes(searchTerm))
      .map((application) => {
        return {
          applicationObj: application,
          selected: this.selectedApplicationIds.has(application.applicationId),
        }
      })
      .sort((applicationLeft, applicationRight) => {
        if (applicationLeft.applicationObj.applicationId === this.application.applicationId) {
          return -1
        }
        if (applicationRight.applicationObj.applicationId === this.application.applicationId) {
          return 1
        }
        if (applicationLeft.applicationObj.name < applicationRight.applicationObj.name) {
          return -1
        }
        return 1
      })
  }

  get totalEligibleApplicationsCount(): number {
    return (
      this._applications?.filter(
        (app) => this.applicationIsCandidate(app) && this.hasRequiredAccessForOperation(app.applicationId)
      ).length ?? 0
    )
  }

  applicationTrackBy(index: number, item: { applicationObj: Application; selected: boolean }) {
    return item.applicationObj.applicationId
  }

  get selectedApplications() {
    return this._applications.filter((application) => this.selectedApplicationIds.has(application.applicationId))
  }

  // Shared gating for the primary launch action: at least one selected app,
  // not currently launching, and every selected app has a resolvable RP (and
  // is not still refreshing its RP list). Subclasses layer on top of this.
  get canLaunch(): boolean {
    if (this.launching) return false
    if (this.selectedApplications.length === 0) return false
    return this.selectedApplications.every(
      (app) => !this.isRefreshingApplicationRecoveryPoints(app) && !!this.computeApplicationRecoveryPoint(app)
    )
  }

  protected abstract applicationIsCandidate(application: Application)

  /**
   * Get the required permission for this operation
   * Subclasses must override this to specify which permission is needed
   */
  protected abstract getRequiredPermission(): AppPermission

  /**
   * Check if user has the required permission for the operation on a specific application
   */
  protected hasRequiredAccessForOperation(applicationId: string): boolean {
    // If RBAC is not enabled, allow all operations
    if (!this.accountService.rbacEnabled) {
      return true
    }

    // If permissions haven't loaded yet, allow the application (permissions will be checked on actual operation)
    if (!this.appPermissionsMapCache || this.isLegacyAdminCache) {
      return true
    }

    const userPermissions = this.appPermissionsMapCache.get(applicationId)

    // If user has no permissions for this app, deny
    if (!userPermissions || userPermissions.size === 0) {
      return false
    }

    // Check if user has the required permission
    return userPermissions.has(this.getRequiredPermission())
  }

  applicationIsChecked(application: Application) {
    return this.selectedApplicationIds.has(application.applicationId)
  }

  onCancelClick() {
    this.cancel.emit(null)
  }

  isStepOpen(key: string): boolean {
    return this.activeStepKey === key
  }

  toggleStep(key: string): void {
    const wasOpen = this.activeStepKey === key
    this.activeStepKey = wasOpen ? null : key
    if (!wasOpen) this.scrollToStep(key)
  }

  continueToStep(nextKey: string): void {
    this.activeStepKey = nextKey
    this.scrollToStep(nextKey)
  }

  onStepHeaderKeydown(event: KeyboardEvent, key: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.toggleStep(key)
    }
  }

  // setTimeout lets Angular paint the newly expanded body before we scroll,
  // so the target's final position is used instead of its collapsed position.
  // Scoped to elementRef so stacked modals don't fight over the same ID.
  private scrollToStep(key: string): void {
    setTimeout(() => {
      const el = this.elementRef.nativeElement.querySelector(`#modal-step-${key}`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  toggleApplicationSelection(applicationId: string) {
    if (this.selectedApplicationIds.has(applicationId)) {
      this.selectedApplicationIds.delete(applicationId)
    } else {
      this.selectedApplicationIds.add(applicationId)
    }
  }

  get canAdjustRecoveryPoint() {
    return true
  }

  get loading() {
    // Spinner clears once user permissions resolve; the form filters apps by
    // permission so it can't render meaningfully without them.
    return this.appPermissionsMapCache === null
  }

  get recoveryPoints() {
    // We need to aggregate these across all applications and uniquify them
    let allRecoveryPoints = this._applications.reduce((accum: Array<RecoveryPointDto>, application: Application) => {
      if (this.selectedApplicationIds.has(application.applicationId)) {
        let app_rps = application.recoveryPoints || []
        if (this.onlyProtectedRps) {
          app_rps = app_rps.filter((rp) => rp.protected)
        }
        accum = accum.concat(app_rps)
      }
      return accum
    }, [])
    // If multiple applications share a given recovery point, it's in the list multiple times.  Let's uniquify it.
    allRecoveryPoints = uniquify(allRecoveryPoints, (rpDto) => rpDto.recoveryPointId)

    if (!allRecoveryPoints.some((rp) => rp.protected)) {
      // If we don't have any retained recovery points, make sure the checkbox is unchecked
      this.onlyProtectedRps = false
    }

    return allRecoveryPoints
  }

  computeApplicationsRecoveryPoint() {
    for (const app of this._applications) {
      this.computeApplicationRecoveryPoint(app)
    }
  }

  // Gate template error states on this — without it, every selected row
  // flashes "no matching recovery points" red before the initial per-app
  // refresh resolves.
  isRefreshingApplicationRecoveryPoints(application: Application): boolean {
    return !!this._refreshSubscriptions?.has(application.applicationId)
  }

  computeApplicationRecoveryPoint(application: Application) {
    // For this application, find the newest recovery point that is older or equal to the selected recovery point
    let rps = []
    if (!this._refreshSubscriptions.has(application.applicationId)) {
      // copy before sort: the canLaunch getter calls this on every CD cycle,
      // and Array.prototype.sort mutates in place — without the spread we'd
      // be re-sorting (and mutating) application.recoveryPoints repeatedly.
      rps = [...(application.recoveryPoints || [])]
    }
    if (this.onlyProtectedRps) {
      rps = rps.filter((rp) => rp.protected)
    }
    // sort them in decreasing order
    rps = rps.sort((l, r) => r.timestamp.valueOf() - l.timestamp.valueOf())
    // filter to the recovery points that are not newer than the selected recovery point
    if (this.selectedRecoveryPoint) {
      rps = rps.filter((rpDto) => rpDto.timestamp <= this.selectedRecoveryPoint.timestamp)
    }
    if (rps.length > 0) {
      // return the newest of these (first in the reverse-sorted list)
      return rps[0]
    }
    // none work, so return null
    return null
  }

  get recoveryPointSnapshotTime() {
    return this.selectedRecoveryPoint ? this.rpTimeString(this.selectedRecoveryPoint) : ''
  }

  showLock(appRp: RecoveryPointDto) {
    return this.protectRecoveryPointEnabled && appRp.protected
  }

  recoversToTimeString(application: Application, appRp: RecoveryPointDto) {
    if (this.isRealTime) {
      if (application.rpo === 0) {
        return 'Now'
      }
    }
    return this.rpTimeString(appRp)
  }

  rpTimeString(rp: RecoveryPointDto) {
    return shortTimeString(rp.timestamp)
  }

  get recoveryPointTime() {
    if (this.isRealTime) {
      return 'Now'
    }
    return this.recoveryPointSnapshotTime
  }

  recoveryPointChanged(rpSliderChangeEvent: RpSliderChangedEvent) {
    this.selectedRecoveryPoint = rpSliderChangeEvent.recoveryPointDto
    this.isRealTime = rpSliderChangeEvent.isRealTime
  }

  isRealTimeApplication(appliction: Application) {
    return appliction.rpo === 0
  }

  get showRetainedRpsCheckbox() {
    return this.protectRecoveryPointEnabled && this.recoveryPoints.some((rp) => rp.protected)
  }

  get selectedIsRealTime() {
    return this.isRealTime
  }

  get selectedRecoveryPointId() {
    return !!this.selectedRecoveryPoint ? this.selectedRecoveryPoint.recoveryPointId : null
  }

  // Sandbox availability is decided across every selected app's effective RP.
  // The launch operates on all selected apps together, so any SG / IGW / VNet
  // in the set is enough to enable the option. Apps with unresolved RPs
  // (still refreshing) are skipped so a transient null doesn't fall closed.
  protected selectedAppsEffectiveRps(): RecoveryPointDto[] {
    return this.selectedApplications
      .map((app) => this.computeApplicationRecoveryPoint(app))
      .filter((rp): rp is RecoveryPointDto => !!rp)
  }

  get selectedAppsHaveAnyStats(): boolean {
    return this.selectedAppsEffectiveRps().some((rp) => !!rp.stats?.resourceTypeStats)
  }

  get selectedAppsHaveAnyIgw(): boolean {
    return this.selectedAppsEffectiveRps().some((rp) =>
      rp.stats?.resourceTypeStats?.some((s) => s.resourceType === ResourceType.ec2InternetGateway)
    )
  }

  get selectedAppsHaveAnySg(): boolean {
    return this.selectedAppsEffectiveRps().some((rp) =>
      rp.stats?.resourceTypeStats?.some((s) => s.resourceType === ResourceType.ec2SecurityGroup)
    )
  }

  get selectedAppsHaveAnyVnet(): boolean {
    return this.selectedAppsEffectiveRps().some((rp) =>
      rp.stats?.resourceTypeStats?.some((s) => s.resourceType === ResourceType.azureVirtualNetwork)
    )
  }

  // Advanced Sandbox (Network Firewall) requires an IGW in the recovered VPC.
  // Falls closed when RP stats are unavailable — we hide the option until we
  // can verify rather than offering something we might not support.
  get shouldShowAdvancedSandboxOption(): boolean {
    return this.selectedAppsHaveAnyStats && this.selectedAppsHaveAnyIgw
  }

  // Legacy (Security Group-based) Sandbox requires at least one Security Group
  // in the recovered VPC. Falls closed on missing stats for the same reason as
  // shouldShowAdvancedSandboxOption.
  get shouldShowBasicSandboxOption(): boolean {
    return this.selectedAppsHaveAnyStats && this.selectedAppsHaveAnySg
  }

  get enableRealTimeRecovery() {
    // return true if any applications have RPO == 0
    return this._applications.reduce((prev, app) => prev || app.rpo == 0, false)
  }

  get protectRecoveryPointEnabled(): boolean {
    return this.accountService.protectRecoveryPointEnabled
  }

  get recoveryReportsEnabled(): boolean {
    return this.accountService.recoveryReportsEnabled
  }
}
